import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Line, Rect, Transformer, Text } from 'react-konva';
import Konva from 'konva';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  selectInstance,
  selectEquipmentPort,
  setInstancePolygon,
  toggleEquipmentReviewSelection,
  translateInstances,
  updatePort,
} from '../../features/equipment/equipmentSlice';
import {
  useEquipmentDragCompanions,
  useLayoutDragCompanions,
} from '../../hooks/useGroupDragCompanions';
import { clearSelection } from '../../features/selection/selectionSlice';
import { openRightDrawerTab } from '../../features/ui/uiSlice';
import { selectTrackNode } from '../../features/layout/layoutSlice';
import {
  polygonBounds,
  polygonCentroid,
  resolvePorts,
  resolveOverlayDisplayStyle,
  isEquipmentOverlayVisible,
  equipmentProvenance,
  equipmentReviewStatus,
  equipmentInstanceToLayoutElement,
} from '../../utils/equipmentGeometry';
import { hexToRgba } from '../../utils/colorConversion';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { replaySnapshotStore } from '../../store/replaySnapshotStore';
import PortLayer from './PortLayer';
import { Equipment2DShape } from './Amhs2dShapes';
import { EQUIPMENT_2D_COLORS, majorityDockEdge } from './amhs/Equipment2D';
import type { EquipmentInstance } from '../../features/equipment/equipmentTypes';
import { SELECTION_COLOR } from '../../constants';
import { ROTATE_CURSOR, ROTATION_ZONE_SIZE, snapRotationTo45 } from '../../utils/canvasTransformUi';

const HOVER_DELAY_MS = 500;
/** Hide name labels when zoomed out — big win with hundreds of DXF extracts. */
const LABEL_MIN_ZOOM = 0.12;

type Props = {
  /**
   * Spacebar-hold pan. It deliberately never reaches Redux, so the active tool
   * still reads as 'select' and has to be overridden here the same way Canvas
   * and CanvasElement do it.
   */
  spacebarPan?: boolean;
  onContextMenu?: (coords: { clientX: number; clientY: number }, instanceId: string) => void;
  onHover?: (hover: { instanceId: string; sx: number; sy: number }) => void;
  onHoverEnd?: () => void;
};

/**
 * Interactive Konva overlay for identified equipment. Renders footprints only
 * (no icons or persistent labels). Details appear in a delayed hover tooltip.
 */
const EquipmentOverlay: React.FC<Props> = ({
  spacebarPan = false,
  onContextMenu,
  onHover,
  onHoverEnd,
}) => {
  const dispatch = useAppDispatch();
  const instances = useAppSelector((s) => s.equipment.instances);
  const order = useAppSelector((s) => s.equipment.instanceOrder);
  const templates = useAppSelector((s) => s.equipment.templates);
  const visible = useAppSelector((s) => s.equipment.visible);
  const showPorts = useAppSelector((s) => s.equipment.showPorts);
  const showLabels = useAppSelector((s) => s.equipment.showLabels);
  const hiddenProcesses = useAppSelector((s) => s.equipment.hiddenProcesses);
  const selectedId = useAppSelector((s) => s.equipment.selectedInstanceId);
  const selectedIds = useAppSelector((s) => s.equipment.selectedInstanceIds);
  const selectedPortId = useAppSelector((s) => s.equipment.selectedPortId);
  const markedIds = useAppSelector((s) => s.equipment.layoutSaveMarkedIds);
  const reviewSelectedIds = useAppSelector((s) => s.equipment.reviewSelectedIds);
  const portEditId = useAppSelector((s) => s.equipment.portEditInstanceId);
  const drawActive = useAppSelector((s) => s.equipment.draw.active);
  const activeTool = useAppSelector((s) => s.ui.activeTool);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  // Point Draw owns all canvas interaction while active; equipment must not
  // capture pointer events or it would steal clicks from the drawing controller.
  const pointDrawActive = useAppSelector((s) => s.ui.pointDraw.active);
  const trackNodePlacementActive = useAppSelector((s) => s.ui.trackNodePlacementActive);
  // Simulator is read-only: footprints can be hovered and panned over, never
  // selected, dragged or resized.
  const simulationMode = useAppSelector((s) => s.ui.simulationPanelOpen);
  // Redux zoom is debounced during wheel — avoids re-rendering every overlay
  // instance on each tick (Stage scale still tracks viewportStore live).
  const zoom = useAppSelector((s) => s.ui.zoomLevel);
  const simulationRunning = useAppSelector((s) => s.simulation.running);
  const simulationTime = useAppSelector((s) => s.simulation.simulationTime);
  const simulationSource = useAppSelector((s) => s.simulation.source);

  const dxfLayers = useAppSelector((s) => s.floorplan.layers);
  const hiddenEntityTypes = useAppSelector((s) => s.floorplan.hiddenEntityTypes);
  const groupRefs = useRef<Record<string, Konva.Group>>({});
  const transformerRef = useRef<Konva.Transformer>(null);
  const rotationStateRef = useRef<{
    id: string;
    node: Konva.Group;
    instance: EquipmentInstance;
    startAngle: number;
    startRotation: number;
    winUp: () => void;
  } | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingHoverIdRef = useRef<string | null>(null);
  const hoverPointerRef = useRef<{ sx: number; sy: number } | null>(null);
  /** Node position when the current instance drag started, for the group delta. */
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);
  const layoutCompanions = useLayoutDragCompanions();
  const equipmentCompanions = useEquipmentDragCompanions();
  /**
   * Local-space box the Transformer draws around the selected instance. Measured
   * rather than derived from the footprint because ports and the selection ring
   * also grow the box, and the rotation zones must start where that border ends.
   */
  const [selectionBox, setSelectionBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const interactionAvailable = !drawActive && !pointDrawActive && !trackNodePlacementActive;
  const effectiveTool = spacebarPan || simulationMode ? 'pan' : activeTool;
  // Hover previews stay available in Simulator; only selection and resize go away.
  const canHover = (effectiveTool === 'select' || effectiveTool === 'pan') && interactionAvailable;
  const canSelect = canHover && !simulationMode;
  // Pan leaves footprints selectable but inert, so mousedown bubbles to the
  // Stage and a drag that starts over equipment pans instead of moving it.
  const canTransform = effectiveTool === 'select' && interactionAvailable;

  const visibleOrder = useMemo(
    () =>
      order.filter((id) => {
        const inst = instances[id];
        if (!inst) return false;
        return isEquipmentOverlayVisible(inst, {
          overlayVisible: visible,
          dxfLayers,
          hiddenEntityTypes,
          hiddenProcesses,
          layoutElements,
        });
      }),
    [order, instances, dxfLayers, hiddenEntityTypes, hiddenProcesses, visible, layoutElements]
  );

  const occupancyById = useMemo(() => {
    if (!simulationRunning) return null;
    const livePortFoupIds =
      simulationSource === 'live' ? liveOhtRuntimeStore.getFrame().portFoupIds : null;
    const snapshot = replaySnapshotStore.getSnapshot();
    const map = new Map<string, number[]>();
    for (const id of visibleOrder) {
      const inst = instances[id];
      if (!inst) continue;
      const template = inst.templateId ? templates[inst.templateId] : null;
      const ports = resolvePorts(inst, template);
      const occupied: number[] = [];
      ports.forEach((port, index) => {
        if (livePortFoupIds) {
          if ((livePortFoupIds[port.id]?.length ?? 0) > 0) occupied.push(index);
          return;
        }
        const live = snapshot?.ports[port.id];
        if (live && (live.inventory > 0 || live.occupiedFoupIds.length > 0)) {
          occupied.push(index);
        }
      });
      map.set(id, occupied);
    }
    return map;
  }, [simulationRunning, simulationSource, simulationTime, visibleOrder, instances, templates]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const cancelPendingHover = useCallback(() => {
    pendingHoverIdRef.current = null;
    hoverPointerRef.current = null;
    clearHoverTimer();
    onHoverEnd?.();
  }, [clearHoverTimer, onHoverEnd]);

  const scheduleHover = useCallback(
    (instanceId: string, sx: number, sy: number) => {
      clearHoverTimer();
      pendingHoverIdRef.current = instanceId;
      hoverPointerRef.current = { sx, sy };
      hoverTimerRef.current = setTimeout(() => {
        if (pendingHoverIdRef.current !== instanceId || !hoverPointerRef.current) return;
        onHover?.({ instanceId, ...hoverPointerRef.current });
      }, HOVER_DELAY_MS);
    },
    [clearHoverTimer, onHover]
  );

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useEffect(() => {
    if (!canHover) cancelPendingHover();
  }, [canHover, cancelPendingHover]);

  // Attach the transformer to the selected instance (unless editing its ports).
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    const selectedInstance = selectedId ? instances[selectedId] : null;
    const canTransformSelected =
      selectedInstance && equipmentReviewStatus(selectedInstance) !== 'potential';
    if (
      selectedId &&
      canTransformSelected &&
      !portEditId &&
      groupRefs.current[selectedId] &&
      canSelect
    ) {
      const node = groupRefs.current[selectedId];
      tr.nodes([node]);
      // Footprints render beneath the layout elements so rails win clicks;
      // the resize/rotate handles still have to stay grabbable above them.
      tr.moveToTop();
      // Same measurement Transformer uses for its own border.
      const box = node.getClientRect({ skipTransform: true, skipShadow: true });
      setSelectionBox((prev) =>
        prev &&
        prev.x === box.x &&
        prev.y === box.y &&
        prev.width === box.width &&
        prev.height === box.height
          ? prev
          : { x: box.x, y: box.y, width: box.width, height: box.height }
      );
    } else {
      tr.nodes([]);
      setSelectionBox(null);
    }
    tr.getLayer()?.batchDraw();
  }, [selectedId, portEditId, canSelect, visibleOrder, instances, zoom, showPorts, selectedPortId]);

  useEffect(() => {
    if (selectedId && !visibleOrder.includes(selectedId)) {
      dispatch(selectInstance(null));
    }
  }, [selectedId, visibleOrder, dispatch]);

  useEffect(() => {
    if (pendingHoverIdRef.current && !visibleOrder.includes(pendingHoverIdRef.current)) {
      cancelPendingHover();
    }
  }, [visibleOrder, cancelPendingHover]);

  /** Bake a node's live transform (move/resize/rotate) back into the instance. */
  const commitNodeTransform = useCallback(
    (node: Konva.Group, instance: EquipmentInstance) => {
      const centroid = polygonCentroid(instance.polygon);
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const rotation = node.rotation();
      const nx = node.x();
      const ny = node.y();
      // Polygon is stored axis-aligned; Group applies rotation for display.
      // Scale in that same local frame, then place at the post-transform origin.
      const nextPolygon = instance.polygon.map((p) => ({
        x: nx + (p.x - centroid.x) * scaleX,
        y: ny + (p.y - centroid.y) * scaleY,
      }));
      node.scaleX(1);
      node.scaleY(1);
      const centroidAfter = polygonCentroid(nextPolygon);
      node.position({ x: centroidAfter.x, y: centroidAfter.y });
      node.rotation(rotation);
      dispatch(
        setInstancePolygon({
          id: instance.id,
          polygon: nextPolygon,
          rotation,
          commit: true,
        })
      );
    },
    [dispatch]
  );

  const finishRotation = useCallback(() => {
    const state = rotationStateRef.current;
    if (!state) return;
    rotationStateRef.current = null;
    const stage = state.node.getStage();
    stage?.off(`mousemove.equipment-rotate-${state.id}`);
    stage?.off(`mouseup.equipment-rotate-${state.id}`);
    if (stage) stage.container().style.cursor = '';
    window.removeEventListener('mouseup', state.winUp);
    state.node.draggable(true);
    commitNodeTransform(state.node, state.instance);
  }, [commitNodeTransform]);

  const handleRotateMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    const state = rotationStateRef.current;
    const stage = state?.node.getStage();
    const pointer = stage?.getPointerPosition();
    if (!state || !stage || !pointer) return;
    const world = stage.getAbsoluteTransform().copy().invert().point(pointer);
    const angle = (Math.atan2(world.y - state.node.y(), world.x - state.node.x()) * 180) / Math.PI;
    const unsnapped = state.startRotation + angle - state.startAngle;
    const next = e.evt.shiftKey ? ((unsnapped % 360) + 360) % 360 : snapRotationTo45(unsnapped);
    state.node.rotation(next);
    transformerRef.current?.forceUpdate();
    state.node.getLayer()?.batchDraw();
  }, []);

  const beginRotation = useCallback(
    (
      e: Konva.KonvaEventObject<MouseEvent>,
      id: string,
      node: Konva.Group,
      instance: EquipmentInstance
    ) => {
      e.cancelBubble = true;
      const stage = node.getStage();
      const pointer = stage?.getPointerPosition();
      if (!stage || !pointer) return;
      const world = stage.getAbsoluteTransform().copy().invert().point(pointer);
      const startAngle = (Math.atan2(world.y - node.y(), world.x - node.x()) * 180) / Math.PI;
      const winUp = () => finishRotation();
      rotationStateRef.current = {
        id,
        node,
        instance,
        startAngle,
        startRotation: node.rotation(),
        winUp,
      };
      node.draggable(false);
      stage.container().style.cursor = ROTATE_CURSOR;
      stage.on(`mousemove.equipment-rotate-${id}`, handleRotateMove);
      stage.on(`mouseup.equipment-rotate-${id}`, finishRotation);
      window.addEventListener('mouseup', winUp);
    },
    [finishRotation, handleRotateMove]
  );

  useEffect(
    () => () => {
      const state = rotationStateRef.current;
      if (!state) return;
      const stage = state.node.getStage();
      stage?.off(`mousemove.equipment-rotate-${state.id}`);
      stage?.off(`mouseup.equipment-rotate-${state.id}`);
      if (stage) stage.container().style.cursor = '';
      window.removeEventListener('mouseup', state.winUp);
      state.node.draggable(true);
      rotationStateRef.current = null;
    },
    []
  );

  const pointerFromEvent = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    return stage?.getPointerPosition() ?? null;
  };

  const beginInstanceDrag = useCallback(
    (id: string, node: Konva.Group) => {
      cancelPendingHover();
      dragOriginRef.current = { x: node.x(), y: node.y() };
      // Dragging an instance outside the current selection moves only that one.
      if (!selectedIds.includes(id)) {
        layoutCompanions.cancel();
        equipmentCompanions.cancel();
        return;
      }
      layoutCompanions.begin();
      equipmentCompanions.begin(id);
    },
    [cancelPendingHover, equipmentCompanions, layoutCompanions, selectedIds]
  );

  const moveInstanceDrag = useCallback(
    (node: Konva.Group) => {
      const origin = dragOriginRef.current;
      if (!origin) return;
      const dx = node.x() - origin.x;
      const dy = node.y() - origin.y;
      layoutCompanions.move(dx, dy);
      equipmentCompanions.move(dx, dy);
    },
    [equipmentCompanions, layoutCompanions]
  );

  const endInstanceDrag = useCallback(
    (node: Konva.Group, instance: EquipmentInstance) => {
      const origin = dragOriginRef.current;
      dragOriginRef.current = null;
      if (!origin) {
        commitNodeTransform(node, instance);
        return;
      }
      const dx = node.x() - origin.x;
      const dy = node.y() - origin.y;
      layoutCompanions.end(dx, dy, Date.now());
      const joinedSnapshot = equipmentCompanions.end(dx, dy);
      if (joinedSnapshot) {
        // Fold this instance into the companions' undo step rather than pushing
        // a second equipment history entry for the same gesture.
        dispatch(translateInstances({ ids: [instance.id], dx, dy }));
        return;
      }
      commitNodeTransform(node, instance);
    },
    [commitNodeTransform, dispatch, equipmentCompanions, layoutCompanions]
  );

  /**
   * Corner rotation zones for the selected instance. They sit in a sibling group
   * rather than inside the instance: nesting them made them part of the group's
   * client rect, which padded the Transformer border out around them and left the
   * zones *inside* the visible selection box.
   */
  const rotationHandles = useMemo(() => {
    if (!selectedId || selectedIds.length !== 1 || !canTransform || portEditId) return null;
    if (!selectionBox) return null;
    const inst = instances[selectedId];
    if (!inst || !visibleOrder.includes(selectedId)) return null;
    if (equipmentReviewStatus(inst) === 'potential') return null;

    const zone = ROTATION_ZONE_SIZE / Math.max(zoom, 0.0001);
    const { x, y, width, height } = selectionBox;
    return {
      instance: inst,
      centroid: polygonCentroid(inst.polygon),
      rotation: inst.rotation,
      zone,
      // Offset by a full half-zone so each square starts exactly on the border
      // and extends outward — never over the selection box itself.
      corners: [
        { x, y, dx: -1, dy: -1 },
        { x: x + width, y, dx: 1, dy: -1 },
        { x: x + width, y: y + height, dx: 1, dy: 1 },
        { x, y: y + height, dx: -1, dy: 1 },
      ],
    };
  }, [
    selectedId,
    selectedIds.length,
    canTransform,
    portEditId,
    selectionBox,
    instances,
    visibleOrder,
    zoom,
  ]);

  if (visibleOrder.length === 0) return null;

  return (
    <>
      {visibleOrder.map((id) => {
        const inst = instances[id];
        if (!inst) return null;

        const template = inst.templateId ? templates[inst.templateId] : null;
        const style = resolveOverlayDisplayStyle(inst, template);
        const ports = resolvePorts(inst, template);
        const centroid = polygonCentroid(inst.polygon);
        const bounds = polygonBounds(inst.polygon);
        const w = bounds.maxX - bounds.minX;
        const h = bounds.maxY - bounds.minY;
        // Font sized to FIT the block (longest word within the width, a couple of lines within
        // the height) so the name label always fits and scales with zoom.
        const label = inst.name ?? '';
        const longestWord = label.split(/\s+/).reduce((m, wd) => Math.max(m, wd.length), 1);
        const labelFont = Math.max(3, Math.min((w * 0.92) / (longestWord * 0.58), h * 0.42, 40));
        const isSelected = selectedIds.includes(id);
        const isExportMarked = markedIds.includes(id);
        const provenance = equipmentProvenance(inst);
        const reviewStatus = equipmentReviewStatus(inst);
        const isPotential = provenance === 'dxf' && reviewStatus === 'potential';
        const isReviewSelected = reviewSelectedIds.includes(id);
        const canShowPorts = provenance !== 'dxf' || reviewStatus === 'accepted';
        // Potential equipment stays a light wash. Accepted/manual equipment
        // gets the schematic body and ports.
        const showSchematicBody = canShowPorts;
        const isPortEdit = id === portEditId;
        // The centroid-pivot correction now lives in the shared builder, so 2D
        // and the 3D layer resolve the same origin.
        const simulationElement = equipmentInstanceToLayoutElement(inst, template);

        const localPoints = inst.polygon.flatMap((p) => [p.x - centroid.x, p.y - centroid.y]);

        // Ports live on the AABB perimeter, expressed in the group's local
        // (centroid-origin) space so PortLayer can constrain them regardless of
        // the instance rotation baked into the enclosing Group.
        const portBox = {
          x: bounds.minX - centroid.x,
          y: bounds.minY - centroid.y,
          w,
          h,
        };

        const strokeW =
          ((isSelected || isReviewSelected ? 2.5 : 1.5) * style.strokeWidthScale) / zoom;
        const showLabel = showLabels && Boolean(label) && zoom >= LABEL_MIN_ZOOM;

        return (
          <Group
            key={id}
            ref={(node) => {
              if (node) groupRefs.current[id] = node;
              else delete groupRefs.current[id];
            }}
            x={centroid.x}
            y={centroid.y}
            rotation={inst.rotation}
            draggable={canTransform && !isPortEdit && !isPotential}
            onMouseDown={(e) => {
              if (!canSelect) return;
              // In Pan mode the Stage must receive mousedown so dragging from
              // directly over equipment still pans the canvas.
              if (canTransform) e.cancelBubble = true;
            }}
            onMouseEnter={(e) => {
              if (!canHover || portEditId) return;
              const pointer = pointerFromEvent(e);
              if (!pointer) return;
              scheduleHover(id, pointer.x, pointer.y);
            }}
            onMouseMove={(e) => {
              if (pendingHoverIdRef.current !== id) return;
              const pointer = pointerFromEvent(e);
              if (pointer) hoverPointerRef.current = { sx: pointer.x, sy: pointer.y };
            }}
            onMouseLeave={() => {
              if (pendingHoverIdRef.current === id) cancelPendingHover();
            }}
            onClick={(e) => {
              if (!canSelect) return;
              e.cancelBubble = true;
              cancelPendingHover();
              dispatch(clearSelection());
              dispatch(selectTrackNode(null));
              if (isPotential) {
                dispatch(toggleEquipmentReviewSelection(id));
                dispatch(openRightDrawerTab('equipment'));
                return;
              }
              dispatch(selectInstance(id));
              dispatch(openRightDrawerTab('properties'));
            }}
            onTap={(e) => {
              if (!canSelect) return;
              e.cancelBubble = true;
              cancelPendingHover();
              dispatch(selectTrackNode(null));
              if (isPotential) {
                dispatch(toggleEquipmentReviewSelection(id));
                dispatch(openRightDrawerTab('equipment'));
                return;
              }
              dispatch(selectInstance(id));
              dispatch(openRightDrawerTab('properties'));
            }}
            onContextMenu={(e) => {
              // Always block the browser canvas menu (Save image as… / Copy image).
              e.evt.preventDefault();
              e.cancelBubble = true;
              if (drawActive || pointDrawActive) return;
              cancelPendingHover();
              dispatch(selectInstance(id));
              onContextMenu?.({ clientX: e.evt.clientX, clientY: e.evt.clientY }, id);
            }}
            onDragStart={(e) => beginInstanceDrag(id, e.target as Konva.Group)}
            onDragMove={(e) => moveInstanceDrag(e.target as Konva.Group)}
            onDragEnd={(e) => endInstanceDrag(e.target as Konva.Group, inst)}
            onTransformStart={() => cancelPendingHover()}
            onTransformEnd={(e) => commitNodeTransform(e.target as Konva.Group, inst)}
          >
            {/* Footprint — schematic EFEM for identified equipment; light wash for DXF */}
            {showSchematicBody ? (
              <Group x={-w / 2} y={-h / 2}>
                <Equipment2DShape
                  w={w}
                  h={h}
                  kind="equipment"
                  selected={isSelected}
                  zoomLevel={zoom}
                  stroke={EQUIPMENT_2D_COLORS.outline}
                  loadPortCount={ports.length}
                  occupiedPorts={occupancyById?.get(id)}
                  dockEdge={majorityDockEdge(ports)}
                  portAnchors={ports.map((port) => ({ rx: port.rx, ry: port.ry }))}
                  simulationElement={simulationElement}
                  portIds={ports.map((port) => port.id)}
                  empty={occupancyById != null && (occupancyById.get(id)?.length ?? 0) === 0}
                  listening
                />
              </Group>
            ) : (
              <Line
                points={localPoints}
                closed
                fill={hexToRgba(style.color, Math.max(style.fillOpacity, 0.28))}
                stroke={
                  isReviewSelected
                    ? '#f59e0b'
                    : hexToRgba(style.color, Math.max(style.strokeOpacity, 0.75))
                }
                strokeWidth={strokeW}
                perfectDrawEnabled={false}
              />
            )}

            {/* Name label, centered in the block */}
            {showLabel && (
              <Text
                text={label}
                x={-w / 2}
                y={-h / 2}
                width={w}
                height={h * 0.42}
                align="center"
                verticalAlign="middle"
                wrap="word"
                fontSize={labelFont}
                fontStyle="bold"
                fill={showSchematicBody ? '#0f172a' : style.labelColor}
                opacity={style.labelOpacity}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}

            {/* Marked for selective layout save (cyan) */}
            {isExportMarked && (
              <Line
                points={localPoints}
                closed
                stroke="#0ea5e9"
                strokeWidth={2.5 / zoom}
                dash={[8 / zoom, 4 / zoom]}
                listening={false}
                perfectDrawEnabled={false}
              />
            )}

            {/* Ports — visible on layout/manual equipment, not raw DXF extracts. */}
            {showPorts && canShowPorts && ports.length > 0 && (
              <PortLayer
                ports={ports}
                box={portBox}
                editable={isPortEdit}
                zoom={zoom}
                selectedPortId={isSelected ? selectedPortId : null}
                onPortClick={
                  simulationMode
                    ? undefined
                    : (portId) => {
                        dispatch(clearSelection());
                        dispatch(selectTrackNode(null));
                        dispatch(selectEquipmentPort({ instanceId: id, portId }));
                        dispatch(openRightDrawerTab('properties'));
                      }
                }
                onPortDragMove={(portId, move) =>
                  dispatch(
                    updatePort({
                      instanceId: id,
                      portId,
                      changes: {
                        rx: move.rx,
                        ry: move.ry,
                        direction: move.direction,
                        edge: move.edge,
                      },
                      skipHistory: true,
                    })
                  )
                }
                onPortDragEnd={(portId, move) =>
                  dispatch(
                    updatePort({
                      instanceId: id,
                      portId,
                      changes: {
                        rx: move.rx,
                        ry: move.ry,
                        direction: move.direction,
                        edge: move.edge,
                      },
                    })
                  )
                }
                parentType={inst.objectType === 'stocker' ? 'stocker' : 'equipment'}
              />
            )}
          </Group>
        );
      })}

      {rotationHandles && (
        <Group
          x={rotationHandles.centroid.x}
          y={rotationHandles.centroid.y}
          rotation={rotationHandles.rotation}
        >
          {rotationHandles.corners.map((corner, index) => (
            <Rect
              key={`rotate-${index}`}
              x={corner.x + corner.dx * rotationHandles.zone * 0.5 - rotationHandles.zone / 2}
              y={corner.y + corner.dy * rotationHandles.zone * 0.5 - rotationHandles.zone / 2}
              width={rotationHandles.zone}
              height={rotationHandles.zone}
              fill="#ffffff"
              opacity={0}
              onMouseEnter={(e) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = ROTATE_CURSOR;
              }}
              onMouseLeave={(e) => {
                if (rotationStateRef.current) return;
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = '';
              }}
              onMouseDown={(e) => {
                const node = groupRefs.current[rotationHandles.instance.id];
                if (node)
                  beginRotation(e, rotationHandles.instance.id, node, rotationHandles.instance);
              }}
              onClick={(e) => {
                e.cancelBubble = true;
              }}
            />
          ))}
        </Group>
      )}

      {selectedId && selectedIds.length === 1 && !portEditId && canSelect && (
        <Transformer
          ref={transformerRef}
          listening={canTransform}
          rotateEnabled={false}
          // Transformer ignores the stage scale, so these are screen pixels
          // and must not be divided by zoom or handles vanish when zoomed in.
          borderStroke={SELECTION_COLOR}
          borderStrokeWidth={2}
          anchorFill="#ffffff"
          anchorStroke={SELECTION_COLOR}
          anchorStrokeWidth={2}
          anchorCornerRadius={2}
          anchorSize={12}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 6 || newBox.height < 6) return oldBox;
            return newBox;
          }}
        />
      )}
    </>
  );
};

export default EquipmentOverlay;
